import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Star, ShoppingCart, Heart, Share2, ChevronLeft, ChevronRight, Check, Truck, Shield, RotateCcw, ZoomIn } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import DeliveryEstimator from './DeliveryEstimator';
import CustomersAlsoBought, { FrequentlyBoughtTogether } from './CustomersAlsoBought';
import ProductReviews from './ProductReviews';
import SocialShare from './SocialShare';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';

// Amazon-style zoom: hover lens on image + large zoom panel to the right (rendered via portal)
const AmazonZoom = ({ src, alt }) => {
  const imgRef = useRef(null);
  const [hovering, setHovering] = useState(false);
  const [lensPos, setLensPos] = useState({ x: 0, y: 0 });
  const [imgRect, setImgRect] = useState({ w: 0, h: 0, top: 0, right: 0 });
  const LENS_RATIO = 0.35;

  const handleMouseMove = useCallback((e) => {
    const el = imgRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const lensW = rect.width * LENS_RATIO;
    const lensH = rect.height * LENS_RATIO;
    let x = e.clientX - rect.left - lensW / 2;
    let y = e.clientY - rect.top - lensH / 2;
    x = Math.max(0, Math.min(x, rect.width - lensW));
    y = Math.max(0, Math.min(y, rect.height - lensH));
    setLensPos({ x, y });
    setImgRect({ w: rect.width, h: rect.height, top: rect.top, right: rect.right });
  }, []);

  const lensW = imgRect.w * LENS_RATIO;
  const lensH = imgRect.h * LENS_RATIO;
  const bgPosX = imgRect.w > 0 ? (lensPos.x / (imgRect.w - lensW)) * 100 : 0;
  const bgPosY = imgRect.h > 0 ? (lensPos.y / (imgRect.h - lensH)) * 100 : 0;

  // Calculate zoom panel: prefer right side, fallback to left if no space
  const spaceRight = typeof window !== 'undefined' ? window.innerWidth - imgRect.right - 20 : 0;
  const panelW = Math.min(420, Math.max(spaceRight, 200));
  const panelH = imgRect.h || 350;
  const panelLeft = spaceRight >= 200 ? imgRect.right + 10 : imgRect.right - imgRect.w - panelW - 10;

  return (
    <>
      <div
        ref={imgRef}
        className="relative w-full h-full cursor-crosshair select-none"
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        onMouseMove={handleMouseMove}
      >
        <img src={src} alt={alt} className="w-full h-full object-contain" draggable={false} />
        {/* Semi-transparent lens rectangle */}
        {hovering && imgRect.w > 0 && (
          <div
            className="absolute pointer-events-none border-2 border-teal-500/80 z-20"
            style={{
              width: lensW,
              height: lensH,
              left: lensPos.x,
              top: lensPos.y,
              background: 'rgba(20, 184, 166, 0.12)',
              boxShadow: '0 0 0 9999px rgba(0,0,0,0.08)',
            }}
          />
        )}
        {/* Hint text */}
        {!hovering && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-black/60 text-white text-[10px] px-2.5 py-1 rounded-full pointer-events-none">
            <ZoomIn className="w-3 h-3" /> Roll over image to zoom
          </div>
        )}
      </div>
      {/* Large zoom panel - rendered via Portal to escape dialog stacking context */}
      {hovering && imgRect.w > 0 && panelW > 100 && createPortal(
        <div
          className="rounded-lg shadow-2xl border border-gray-200 bg-white overflow-hidden pointer-events-none"
          style={{
            position: 'fixed',
            zIndex: 99999,
            width: panelW,
            height: panelH,
            top: imgRect.top,
            left: panelLeft,
            backgroundImage: `url(${src})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: `${(1 / LENS_RATIO) * 100}%`,
            backgroundPosition: `${bgPosX}% ${bgPosY}%`,
          }}
          data-testid="amazon-zoom-panel"
        />,
        document.body
      )}
    </>
  );
};

const QuickViewModal = ({ isOpen, onClose, product, onAddToCart, onQuickView, onWishlist, wishlistItems = [] }) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [showReviews, setShowReviews] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const { user } = useAuth();
  const settings = useSettings();

  const isInWishlist = wishlistItems.some(w => w.product_id === product?.id || w.id === product?.id);

  if (!product) return null;

  // Use actual product images array; fall back to single product.image
  const images = product.images && product.images.length > 0
    ? product.images
    : product.image ? [product.image] : ['https://via.placeholder.com/400'];

  const handleAddToCart = () => {
    onAddToCart({ ...product, quantity });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] p-0 overflow-y-auto">
        <div className="grid md:grid-cols-2 gap-0">
          {/* Image Section */}
          <div className="relative bg-gray-50 p-4 md:p-6">
            {/* Main Image with Amazon-style Zoom */}
            <div className="aspect-square max-h-[40vh] md:max-h-[50vh] rounded-xl overflow-visible bg-white mb-3 mx-auto relative" data-testid="product-main-image">
              <AmazonZoom
                src={images[selectedImage]}
                alt={product.name}
              />
            </div>
            
            {/* Thumbnails */}
            <div className="flex gap-2 justify-center">
              {images.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                    selectedImage === index
                      ? 'border-teal-500 ring-2 ring-teal-200'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>

            {/* Discount Badge */}
            {product.discount && (
              <span className="absolute top-8 left-8 px-3 py-1 bg-rose-500 text-white text-sm font-bold rounded-full">
                {product.discount}% OFF
              </span>
            )}
          </div>

          {/* Details Section */}
          <div className="p-4 md:p-6 flex flex-col">
            {/* Category */}
            <span className="text-xs text-teal-600 font-medium mb-1 capitalize">
              {product.category?.replace('-', ' ') || 'Healthcare'}
            </span>
            
            {/* Name */}
            <h2 className="text-xl font-bold text-gray-800 mb-1">
              {product.name}
            </h2>

            {/* Generic Name / Composition */}
            {product.generic_name && (
              <p className="text-xs text-gray-600 mb-2 p-1.5 bg-gray-50 rounded-lg">
                <span className="font-medium text-gray-700">Composition:</span> {product.generic_name}
              </p>
            )}

            {/* Monthly Offer Badge */}
            {product.is_monthly_offer && (
              <div className="inline-flex items-center gap-1 mb-2 px-2 py-1 bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-bold rounded-full">
                This Month&apos;s Special Offer
              </div>
            )}

            {/* Rating */}
            <div className="flex items-center gap-2 mb-3">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-3.5 h-3.5 ${
                      star <= Math.floor(product.rating)
                        ? 'text-yellow-400 fill-yellow-400'
                        : 'text-gray-200'
                    }`}
                  />
                ))}
              </div>
              <span className="text-xs text-gray-500">
                {product.rating} ({product.reviewCount} reviews)
              </span>
            </div>

            {/* Price */}
            <div className="flex items-baseline gap-2 mb-3">
              <span className="text-2xl font-bold text-gray-800">
                ${product.price.toFixed(2)}
              </span>
              {product.originalPrice && (
                <span className="text-sm text-gray-400 line-through">
                  ${product.originalPrice.toFixed(2)}
                </span>
              )}
              {product.discount && (
                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">
                  Save ${(product.originalPrice - product.price).toFixed(2)}
                </span>
              )}
            </div>

            {/* Stock Status */}
            <div className={`flex items-center gap-2 mb-3 ${
              product.inStock ? 'text-emerald-600' : 'text-rose-500'
            }`}>
              <div className={`w-2 h-2 rounded-full ${
                product.inStock ? 'bg-emerald-500' : 'bg-rose-500'
              }`} />
              <span className="text-sm font-medium">
                {product.inStock ? 'In Stock - Ready to Ship' : 'Out of Stock'}
              </span>
            </div>

            {/* Quantity Selector */}
            {product.inStock && (
              <div className="flex items-center gap-3 mb-3">
                <span className="text-sm text-gray-600 font-medium">Quantity:</span>
                <div className="flex items-center border border-gray-200 rounded-lg">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="px-2.5 py-1.5 hover:bg-gray-100 transition-colors text-sm"
                  >
                    -
                  </button>
                  <span className="px-3 py-1.5 font-medium text-sm">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="px-2.5 py-1.5 hover:bg-gray-100 transition-colors text-sm"
                  >
                    +
                  </button>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2 mb-3">
              {product.inStock ? (
                <button
                  onClick={handleAddToCart}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-teal-600 text-white font-semibold rounded-xl hover:bg-teal-700 transition-colors text-sm"
                >
                  <ShoppingCart className="w-4 h-4" />
                  Add to Cart
                </button>
              ) : (
                <button className="flex-1 py-2.5 bg-gray-100 text-gray-600 font-semibold rounded-xl cursor-not-allowed text-sm">
                  Out of Stock
                </button>
              )}
              <button
                onClick={() => onWishlist?.(product)}
                className={`p-2.5 border rounded-xl transition-colors ${
                  isInWishlist
                    ? 'border-rose-300 bg-rose-50 hover:bg-rose-100'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
                data-testid="wishlist-btn"
                title={isInWishlist ? 'Remove from Wishlist' : 'Add to Wishlist'}
              >
                <Heart className={`w-4 h-4 ${isInWishlist ? 'text-rose-500 fill-rose-500' : 'text-gray-600'}`} />
              </button>
              <button
                onClick={() => setShowShareMenu(!showShareMenu)}
                className="p-2.5 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                data-testid="share-btn"
                title="Share this product"
              >
                <Share2 className="w-4 h-4 text-gray-600" />
              </button>
            </div>

            {/* Share popup (toggles on share button click) */}
            {showShareMenu && (
              <div className="p-2 bg-gray-50 rounded-xl border mb-3 animate-in fade-in slide-in-from-top-2 duration-200">
                <SocialShare
                  title={product?.name}
                  description={`Check out ${product?.name} at ${settings.site_name || 'our store'} - $${product?.price?.toFixed(2)}`}
                  compact={true}
                />
              </div>
            )}

            {/* Delivery Estimator */}
            <div className="mb-3">
              <DeliveryEstimator inStock={product.inStock} />
            </div>

            {/* Features */}
            <div className="grid grid-cols-3 gap-1 pt-3 border-t">
              <div className="flex flex-col items-center text-center p-1.5">
                <Truck className="w-4 h-4 text-teal-600 mb-0.5" />
                <span className="text-[10px] text-gray-600">Free Shipping</span>
              </div>
              <div className="flex flex-col items-center text-center p-1.5">
                <Shield className="w-4 h-4 text-teal-600 mb-0.5" />
                <span className="text-[10px] text-gray-600">Genuine Product</span>
              </div>
              <div className="flex flex-col items-center text-center p-1.5">
                <RotateCcw className="w-4 h-4 text-teal-600 mb-0.5" />
                <span className="text-[10px] text-gray-600">Easy Returns</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Frequently Bought Together */}
        {product?.id && (
          <div className="border-t p-4 bg-gray-50 dark:bg-gray-800">
            <FrequentlyBoughtTogether 
              productId={product.id} 
              onAddToCart={onAddToCart}
              onQuickView={onQuickView}
            />
          </div>
        )}

        {/* Reviews Section */}
        {product?.id && (
          <div className="border-t p-4">
            <button
              onClick={() => setShowReviews(!showReviews)}
              className="w-full flex items-center justify-between py-2 text-sm font-medium text-gray-800 dark:text-white hover:text-teal-600 transition-colors"
              data-testid="toggle-reviews-btn"
            >
              <span className="flex items-center gap-2">
                <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                Customer Reviews
              </span>
              <ChevronRight className={`w-4 h-4 transition-transform ${showReviews ? 'rotate-90' : ''}`} />
            </button>
            {showReviews && (
              <div className="mt-3">
                <ProductReviews productId={product.id} userId={user?.id} showForm={!!user} />
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuickViewModal;
